import React, { memo, useCallback, useMemo, useState } from 'react'
import { Modal, Typography } from '@mui/material'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import { Button } from 'src/components/shared/Button'
import { BasicTextArea } from 'src/components/shared/form/BasicTextArea'
import { CancelSmallMinor } from '@shopify/polaris-icons'
import VoidPaymentModalImage from 'src/assets/images/not-found.png'

const VOID_NOTE_MAX_LENGTH = 500

interface Props {
  isOpen: boolean
  onClose(): void
  onSubmit(voidNote: string): Promise<void>
}

const VoidPaymentModalComponent = ({ isOpen, onClose, onSubmit }: Props) => {
  const container = useMemo(() => document.getElementById('root'), [])

  const [isLoading, setIsLoading] = useState(false)
  const [voidNote, setVoidNote] = useState('')

  const handleClose = useCallback(() => {
    setVoidNote('')
    onClose()
  }, [onClose])

  const handleVoidNoteChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setVoidNote(event.target.value)
  }, [])

  const handleSubmit = useCallback(async () => {
    setIsLoading(true)

    try {
      await onSubmit(voidNote)
      setVoidNote('')
    } finally {
      setIsLoading(false)
    }
  }, [onSubmit, voidNote])

  return (
    <Modal
      container={container}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="void-payment-modal-title"
      aria-describedby="void-payment-modal-description"
      css={tw`flex justify-center items-center backdrop-blur p-4 z-[2001]`}
    >
      <div
        data-testid="void-payment-modal"
        css={tw`w-[422px] flex flex-col bg-white rounded-lg relative`}
      >
        <button
          data-testid="void-payment-modal-close-btn"
          css={tw`absolute top-4 right-4 p-1 rounded bg-[#EDEDED] h-[28px] border-0 hover:bg-[#DAD9E0] active:bg-[#B4AFC0] cursor-pointer`}
          aria-label="Close"
          onClick={handleClose}
        >
          <CancelSmallMinor width={20} height={20} />
        </button>
        <div css={tw`flex justify-center mt-[44px]`}>
          <img src={VoidPaymentModalImage} alt="" css={tw`w-[210px] h-[164px] object-contain`} />
        </div>
        <Typography id="void-payment-modal-title" css={tw`mb-0.5 text-center`} variant="h4">
          Void Settlement?
        </Typography>
        <Typography
          id="void-payment-modal-description"
          variant="body2"
          color={colors.SHADES_GREY_400}
          css={tw`mx-4 mb-2 text-center`}
        >
          This action will void the settlement. If the settlement has already been processed, a
          refund will be issued automatically. Do you want to continue?
        </Typography>
        <div css={tw`mx-4 mb-6`}>
          <BasicTextArea<{ voidNote: string }>
            name="voidNote"
            placeholder="Reason for voiding (required)"
            minRows={3}
            value={voidNote}
            inputProps={{
              maxLength: VOID_NOTE_MAX_LENGTH,
              'data-testid': 'void-payment-modal-void-note',
            }}
            css={tw`w-full`}
            onChange={handleVoidNoteChange}
          />
          <Typography variant="subtitle2" color={colors.SHADES_GREY_400} css={tw`text-right`}>
            {voidNote.length}/{VOID_NOTE_MAX_LENGTH}
          </Typography>
        </div>
        <div
          css={tw`flex justify-between items-center px-4 py-3 border-0 border-t border-solid border-[#EDEDED]`}
        >
          <Button
            className={tw`py-2 px-8`}
            disabled={isLoading}
            onClick={handleClose}
            color="primary"
            size="small"
            data-testid="void-payment-modal-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            className={tw`py-2 px-8 font-medium`}
            loading={isLoading}
            disabled={!voidNote.trim()}
            onClick={handleSubmit}
            color="light"
            size="small"
            data-testid="void-payment-modal-submit-btn"
          >
            Void Settlement
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export const VoidPaymentModal = memo(VoidPaymentModalComponent)
